import { Upload, message, Image, Button, UploadProps } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiOutlineLoading3Quarters, AiOutlinePlus, AiOutlineUpload } from "react-icons/ai";
import { AiOutlineDelete } from "react-icons/ai";

interface UploadInputPropsType {
  alt?: string;
  value?: any;
  accept?: string;
  listType?: UploadProps["listType"];
  onDelete?: () => void;
}

export default function UploadInput(props: UploadInputPropsType) {
  const { alt, value, accept, listType = "picture-card", onDelete, ...other } = props;
  const isPdf = accept === ".pdf";
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState("");
  const [fileName, setFileName] = useState("");
  const { t } = useTranslation();
  const isJpgOrPng = useRef<boolean>(true);
  const isLt4M = useRef<boolean>(true);

  const getBase64 = (img: File, callback: (imageUrl: string) => void) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") {
        callback(reader.result);
      }
    });
    reader.readAsDataURL(img);
  };

  useEffect(() => {
    if (isText && value && typeof value === "object" && value.file?.name) {
      setFileName(value.file.name);
      setLoading(false);
    } else if (value && isJpgOrPng.current && isLt4M.current && typeof value === "string") {
      setImage(value);
      setLoading(false);
    } else if (value && typeof value === "object" && value.file?.originFileObj) {
      getBase64(value.file.originFileObj, (imageUrl: string) => {
        setLoading(false);
        setImage(imageUrl);
      });
    }
  }, [value]);

  const beforeUpload = (file: File) => {
    if (isPdf) {
      return true;
    }
    isJpgOrPng.current = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng.current) {
      message.error(t("You can only upload JPG/PNG file!"));
    }
    isLt4M.current = file.size / 1024 / 1024 < 4;
    if (!isLt4M.current) {
      message.error(t("Image must be smaller than 4MB!"));
    }
    return isJpgOrPng.current && isLt4M.current;
  };

  const handleChange = (info: any) => {
    if (info.file.originFileObj) {
      if (isText) {
        setFileName(info.file.name);
        setLoading(false);
      } else {
        getBase64(info.file.originFileObj, (imageUrl: string) => {
          setLoading(false);
          setImage(imageUrl);
        });
      }
    }
  };
  const isText = listType === "text";

  const uploadButton = isText ? (
    <Button
      icon={<AiOutlineUpload style={{ display: "flex" }} />}
      style={{ display: "flex", alignItems: "center", gap: 6 }}
    >
      {t("Upload")}
    </Button>
  ) : (
    <div>
      {loading ? <AiOutlineLoading3Quarters /> : <AiOutlinePlus />}
      <div style={{ marginTop: 8 }}>{t("Upload")}</div>
    </div>
  );

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <Upload
        listType={listType}
        className="avatar-uploader"
        showUploadList={false}
        accept={accept || "image/*"}
        beforeUpload={beforeUpload}
        onChange={handleChange}
        customRequest={() => false}
        openFileDialogOnClick={true}
        {...other}
      >
        {isText && fileName ? (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {fileName}
            <Button
              type="text"
              danger
              size="small"
              icon={<AiOutlineDelete />}
              onClick={e => {
                e.stopPropagation();
                setFileName("");
              }}
            />
          </span>
        ) : image ? (
          <Image src={image} alt={alt} style={{ maxHeight: "100px" }} preview={false} />
        ) : (
          uploadButton
        )}
      </Upload>
      {image && onDelete && (
        <Button
          danger
          size="small"
          icon={<AiOutlineDelete color="#8c8c8c" />}
          onClick={e => {
            e.stopPropagation();
            setImage("");
            onDelete();
          }}
          style={{
            position: "absolute",
            top: 0,
            right: "-20px",
            border: "1px dashed #8c8c8c",
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
          title={t("Delete image")}
        />
      )}
    </div>
  );
}
